import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Clock, Award, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { fetchPublicCourse } from "../lib/site";

export default function SiteCourseDetail() {
  const { cid } = useParams();
  const [course, setCourse] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    fetchPublicCourse(cid).then(setCourse).catch(() => setMissing(true));
  }, [cid]);

  if (missing) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="course-not-found">
        <p className="text-slate-500 mb-4">This course is not available.</p>
        <Link to="/courses" className="text-blue-800 font-medium hover:underline">← Back to courses</Link>
      </div>
    );
  }
  if (!course) {
    return <div className="py-20 text-center text-slate-400"><Loader2 className="animate-spin inline" size={22} /></div>;
  }

  const terms = {};
  (course.subjects || []).forEach((s) => {
    const t = s.term || 1;
    (terms[t] = terms[t] || []).push(s);
  });
  const termKeys = Object.keys(terms).sort();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="course-detail">
      <Link to="/courses" className="inline-flex items-center gap-1.5 text-sm text-blue-800 font-medium hover:underline mb-6" data-testid="course-back">
        <ArrowLeft size={15} /> All courses
      </Link>

      {course.image && (
        <img src={course.image} alt={course.name} data-testid="course-image"
          className="w-full h-56 sm:h-64 object-cover rounded-2xl mb-6" />
      )}

      <div className="rounded-2xl bg-gradient-to-br from-blue-950 via-blue-900 to-blue-700 text-white p-8 sm:p-10 mb-8">
        <h1 className="font-display text-2xl sm:text-4xl font-bold" data-testid="course-name">{course.name}</h1>
        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          {course.short_name && (
            <span className="inline-flex items-center gap-1.5 bg-white text-blue-900 font-semibold px-3 py-1.5 rounded-full" data-testid="course-short-name">
              {course.short_name}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full">
            <Clock size={14} /> {course.duration}
          </span>
          {course.course_type && (
            <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full">
              <Award size={14} /> {course.course_type}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full">
            <BookOpen size={14} /> {(course.subjects || []).length} subjects
          </span>
        </div>
      </div>

      {course.description && (
        <section className="mb-8">
          <h2 className="font-display text-xl font-bold text-slate-900 mb-2">Course Details</h2>
          <p className="text-slate-600 leading-relaxed whitespace-pre-line" data-testid="course-description">{course.description}</p>
        </section>
      )}
      {course.eligibility && (
        <section className="mb-8 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Eligibility</h2>
          <p className="text-slate-700" data-testid="course-eligibility">{course.eligibility}</p>
        </section>
      )}

      {termKeys.length > 0 && (
        <section className="mb-8">
          <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Course Structure</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {termKeys.map((t) => (
              <div key={t} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`course-term-${t}`}>
                <div className="text-sm font-semibold text-blue-900 mb-3">Term {t}</div>
                <ul className="space-y-2">
                  {terms[t].map((s) => (
                    <li key={s.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-800 shrink-0" />
                      {s.name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="rounded-xl bg-blue-50 border border-blue-100 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <p className="text-sm text-blue-900 flex-1">
          Interested in {course.name}? Send us an enquiry — or verify an existing certificate online.
        </p>
        <div className="flex gap-2.5 shrink-0">
          <Link
            to={`/contact?course=${encodeURIComponent(course.name)}`}
            data-testid="course-enquire-btn"
            className="inline-flex items-center gap-2 bg-white text-blue-900 border border-blue-200 text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-blue-100 transition-soft"
          >
            <Mail size={15} /> Enquire Now
          </Link>
          <Link
            to="/verify"
            data-testid="course-verify-link"
            className="inline-flex items-center gap-2 bg-blue-800 text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-blue-900 transition-soft"
          >
            <ShieldCheck size={15} /> Verify Certificate
          </Link>
        </div>
      </div>
    </div>
  );
}
